import { useCallback, useEffect, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  BulkCreateProjectManagementPlannedRecordMutationVariables,
  ProjectForSelectFragment,
  ProjectsInfoSidepanelDocument,
  SubprojectForSelectFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import DurationInput from 'modules/core/components/DurationInput'
import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { ProjectSelector } from 'modules/project_management/components/selectors/ProjectSelector'
import WeekDaysSelector from 'modules/project_management/components/selectors/WeekDaysSelector'

interface Props {
  plannedRecord: BulkCreateProjectManagementPlannedRecordMutationVariables
  onChange: (plannedRecord: BulkCreateProjectManagementPlannedRecordMutationVariables) => void
  existingId?: number
  employee?: EmployeeForSelectFragmentNoAccess | null
  project?: ProjectForSelectFragment
  subproject?: SubprojectForSelectFragment
}

export const ProjectHoursAssignationForm = ({
  plannedRecord,
  onChange,
  existingId,
  project,
  subproject,
  employee,
}: Props) => {
  const [selectedProject, setSelectedProject] = useState<ProjectForSelectFragment | null>(
    project || null
  )
  const [selectedSubproject, setSelectedSubproject] = useState<SubprojectForSelectFragment | null>(
    subproject || null
  )

  const employeeId = employee?.id
  const isPreselectedEmployee = !!employee && !existingId

  const { employee: currentEmployee } = useCurrentSession()

  const handleProjectChange = (value: ProjectForSelectFragment | null) => {
    track(trackingEvents.projectManagement.schedule.selectProjectInModal, {})
    setSelectedProject(value)
    setSelectedSubproject(null)
    setProjectWorkerIds([])
    if (!isPreselectedEmployee) {
      setSelectedEmployees([])
    }
  }

  const handleSubprojectChange = (value: SubprojectForSelectFragment | null) => {
    track(trackingEvents.projectManagement.schedule.selectSubprojectInModal, {})
    setSelectedSubproject(value)
  }

  const { data } = useQuery(ProjectsInfoSidepanelDocument, {
    variables: {
      projectId: selectedProject?.id ? [Number(selectedProject.id)] : [],
    },
  })

  const [projectWorkerIds, setProjectWorkerIds] = useState<number[]>(
    plannedRecord.projectWorkerIds ? plannedRecord.projectWorkerIds : []
  )

  const [selectedEmployees, setSelectedEmployees] = useState<number[]>(
    employeeId ? [employeeId] : []
  )

  const handleOnChangeStartDate = (value: string) => {
    track(trackingEvents.projectManagement.schedule.selectDatesInModal, { from: value })
    onChange({ ...plannedRecord, startDate: value })
  }

  const handleOnChangeEndDate = (value: string) => {
    track(trackingEvents.projectManagement.schedule.selectDatesInModal, { to: value })
    onChange({ ...plannedRecord, endDate: value })
  }

  const handleOnChangeDailyMinutes = (value: number) => {
    track(trackingEvents.projectManagement.schedule.selectHoursInModal, {})
    onChange({ ...plannedRecord, dailyMinutes: value })
  }

  const handleEmployeeChange = (employeeIds: number[]) => {
    track(trackingEvents.projectManagement.schedule.selectEmployeeInModal, {})
    handleProjectWorkerChange(employeeIds)
  }

  const handleOnChangeWeekDays = (value: number[]) => {
    onChange({ ...plannedRecord, weekDays: value })
  }

  const projectWorkers = data?.projectManagement.projectWorkers

  const handleProjectWorkerChange = useCallback(
    (employeeIds: number[]) => {
      const selectedProjectWorkers =
        projectWorkers?.filter((worker) => {
          const workerId = worker?.employee?.id
          return workerId && employeeIds.includes(workerId)
        }) ?? []

      const workerIds = selectedProjectWorkers
        .map((worker) => worker?.id)
        .filter((id): id is number => !!id)

      setProjectWorkerIds(workerIds)
      setSelectedEmployees(employeeIds)
    },
    [projectWorkers]
  )

  useEffect(() => {
    if (isPreselectedEmployee) {
      handleProjectWorkerChange([employee?.id])
    }
  }, [selectedProject, projectWorkers, employee, handleProjectWorkerChange, isPreselectedEmployee])

  useEffect(() => {
    onChange({ ...plannedRecord, projectWorkerIds: projectWorkerIds })
  }, [projectWorkerIds])

  useEffect(() => {
    onChange({ ...plannedRecord, subprojectId: selectedSubproject?.id })
  }, [selectedSubproject])

  const employeeIds = projectWorkers?.map((worker) => worker?.employee?.id)?.filter(Boolean)

  return (
    <Box gap="s24">
      <ProjectSelector
        mandatory
        notClosed
        disabled={!!existingId}
        value={selectedProject}
        onChange={handleProjectChange}
        onChangeSubproject={handleSubprojectChange}
        subprojectValue={selectedSubproject}
        subprojectNotClosed
        skipViewerByEmployeeId={currentEmployee?.id}
        assignedEmployeeIds={isPreselectedEmployee ? [employee?.id] : []}
      />
      <Box flexDirection="row" gap="s8">
        <DayPicker
          mandatory
          label={i18n.t('common.from')}
          value={plannedRecord.startDate}
          maxDate={plannedRecord?.endDate ? moment(plannedRecord?.endDate, 'L') : undefined}
          onChange={(value) => handleOnChangeStartDate(value)}
        />
        <DayPicker
          mandatory
          label={i18n.t('common.to')}
          value={plannedRecord?.endDate}
          onChange={(value) => handleOnChangeEndDate(value)}
          minDate={plannedRecord?.startDate ? moment(plannedRecord?.startDate, 'L') : undefined}
        />
      </Box>
      <WeekDaysSelector value={plannedRecord.weekDays ?? []} onChange={handleOnChangeWeekDays} />
      <DurationInput
        readonly={false}
        label={i18n.t('project_management.schedule.sidepanel.daily_minutes_label')}
        hideMinute={false}
        minutes={plannedRecord.dailyMinutes}
        onChange={(value) => handleOnChangeDailyMinutes(value)}
      />

      {existingId || isPreselectedEmployee ? (
        <EmployeeSelectorV2
          mandatory
          disabled={isPreselectedEmployee}
          onChange={(state) => state && handleProjectWorkerChange([state])}
          value={selectedEmployees[0]}
          forcedFilters={{ employeeIds }}
        />
      ) : (
        employeeIds &&
        employeeIds.length > 0 && (
          <EmployeeSelectorV2
            mandatory
            multiple
            disabled={isPreselectedEmployee}
            onChange={(state: EmployeeSelectorV2MultiSelectState) => {
              if (!state) return
              if (state.type === 'manual') {
                handleEmployeeChange(state.ids)
              } else {
                const allIds = employeeIds.filter((id): id is number => id !== undefined)
                const selectedIds = allIds.filter((id) => !state.excludedIds.includes(id))
                handleEmployeeChange(selectedIds)
              }
            }}
            value={selectedEmployees}
            forcedFilters={{ employeeIds }}
          />
        )
      )}
    </Box>
  )
}
